import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/auth';
import { apiClient, FILE_BASE_URL } from '../../api/axios';
import { authorAuthService, ProfileData } from '../author/services/authorAuth';
import { lookupService, LookupDto } from '../../services/lookupService';
import { useNotification } from './Notifications';

const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

interface BankAccountDetail {
    id: string;
    accountName: string;
    accountNumber: string;
    bankName: string;
}

type ProfileTab = 'personal' | 'bank';

const Profile: React.FC = () => {
    const { user, updateProfileCompletion } = useAuth();
    const isReviewer = user?.role === 'accessor';
    // ES role (roleId 8) must upload a signature — required, and only shown for ES.
    const isES = user?.roleId === 8;
    const [activeTab, setActiveTab] = useState<ProfileTab>('personal');
    const location = useLocation();
    const { showNotification } = useNotification();
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState<ProfileData | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [signatureFile, setSignatureFile] = useState<File | null>(null);

    // Absolute URL of the ES user's stored signature (field lives at the profile root).
    const signaturePath = profile?.signature || null;
    const signatureUrl = signaturePath
        ? (signaturePath.startsWith('http') ? signaturePath : `${FILE_BASE_URL}/${signaturePath.replace(/^\//, '')}`)
        : null;

    const [titles, setTitles] = useState<LookupDto[]>([]);
    const [states, setStates] = useState<LookupDto[]>([]);
    const [qualifications, setQualifications] = useState<LookupDto[]>([]);
    const [ranks, setRanks] = useState<LookupDto[]>([]);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        otherName: '',
        email: '',
        phoneNo: '',
        titleId: null as number | null,
        institutionId: null as number | null,
        address: '',
        qualificationId: null as number | null,
        sexId: null as number | null,
        rankId: null as number | null,
        stateId: null as number | null,
        institutionCategoryId: '' as string | number,
        institutionIsPublic: '' as string // "true" or "false"
    });

    const [institutionCategories] = useState<LookupDto[]>([
        { id: 1, name: 'University', active: true },
        { id: 2, name: 'Polytechnic', active: true },
        { id: 3, name: 'College', active: true },
    ]);
    const [institutions, setInstitutions] = useState<LookupDto[]>([]);

    useEffect(() => {
        fetchProfile();
        fetchLookups();
        if (location.state?.fromModal) {
            setIsEditing(true);
        }
        if (location.state?.tab === 'bank') {
            setActiveTab('bank');
        }
    }, [location.state]);

    const fetchLookups = async () => {
        try {
            const [titlesData, statesData, qualificationsData, ranksData] = await Promise.all([
                lookupService.getTitles(),
                lookupService.getStates(),
                lookupService.getQualifications(),
                lookupService.getRanks()
            ]);
            setTitles(titlesData);
            setStates(statesData);
            setQualifications(qualificationsData);
            setRanks(ranksData);
        } catch (error) {
            }
    };

    useEffect(() => {
        const fetchInstitutions = async () => {
            if (formData.institutionCategoryId && formData.institutionIsPublic) {
                try {
                    const instData = await lookupService.getInstitutions(
                        Number(formData.institutionCategoryId),
                        formData.institutionIsPublic === 'true'
                    );
                    setInstitutions(instData || []);
                    // Reset institutionId when category or type changes during editing
                    if (isEditing) {
                        setFormData(prev => ({ ...prev, institutionId: null }));
                    }
                } catch (error) {
                    setInstitutions([]);
                }
            } else {
                setInstitutions([]);
            }
        };
        fetchInstitutions();
    }, [formData.institutionCategoryId, formData.institutionIsPublic]);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await authorAuthService.getUserProfile();
            if (response.isSuccess && response.data) {
                const data = response.data;
                setProfile(data);

                if (data.person) {
                    setFormData({
                        firstName: data.person.firstName || '',
                        lastName: data.person.lastName || '',
                        otherName: data.person.otherName || '',
                        email: data.person.email || '',
                        phoneNo: data.person.phoneNo || '',
                        titleId: data.person.titleId || null,
                        institutionId: data.person.institutionId || null,
                        // dob intentionally omitted per requirements
                        address: data.person.address || '',
                        qualificationId: data.person.qualificationId || null,
                        sexId: data.person.sexId || null,
                        rankId: data.person.rankId || null,
                        stateId: data.person.stateId || null,
                        institutionCategoryId: data.person.institution?.institutionCategory === 'University' ? 1 :
                            data.person.institution?.institutionCategory === 'Polytechnic' ? 2 :
                                data.person.institution?.institutionCategory === 'College' ? 3 : '',
                        institutionIsPublic: data.person.institution?.isPublic !== undefined ? String(data.person.institution.isPublic) : ''
                    });
                }
            }
        } catch (error) {
            showNotification('error', { title: 'Error', message: 'Failed to fetch profile details' });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const isOtherInstitution = profile?.person?.isOtherInstitution === true;

    const isFormValid = () => {
        const requiredFields = [
            'firstName',
            'lastName',
            'phoneNo',
            'titleId',
            'address',
            'qualificationId',
            'sexId',
            'rankId',
            'stateId',
            ...(isOtherInstitution
                ? []
                : ['institutionId', 'institutionCategoryId', 'institutionIsPublic']),
        ];

        const baseValid = requiredFields.every(field => {
            const value = (formData as any)[field];
            return value !== null && value !== undefined && value.toString().trim() !== '';
        });

        // ES must have a signature — either a newly selected file or one already on file.
        const signatureValid = !isES || !!signatureFile || !!signaturePath;

        return baseValid && signatureValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isES && !signatureFile && !signaturePath) {
            showNotification('warning', { title: 'Signature Required', message: 'Please upload your signature.' });
            return;
        }

        try {
            setSaving(true);

            // The API now expects multipart/form-data.
            const fd = new FormData();
            fd.append('FirstName', formData.firstName);
            fd.append('LastName', formData.lastName);
            fd.append('OtherName', formData.otherName || '');
            fd.append('Email', formData.email);
            fd.append('PhoneNo', formData.phoneNo);
            if (formData.titleId != null) fd.append('TitleId', String(Number(formData.titleId)));
            if (formData.institutionId != null) fd.append('InstitutionId', String(Number(formData.institutionId)));
            fd.append('Address', formData.address || '');
            if (formData.qualificationId != null) fd.append('QualificationId', String(Number(formData.qualificationId)));
            if (formData.sexId != null) fd.append('SexId', String(Number(formData.sexId)));
            if (formData.rankId != null) fd.append('RankId', String(Number(formData.rankId)));
            if (formData.stateId != null) fd.append('StateId', String(Number(formData.stateId)));
            fd.append('AffiliateInstitution', profile?.person?.affiliateInstitution || '');
            fd.append('IsOtherInstitution', String(profile?.person?.isOtherInstitution === true));
            // DOB intentionally omitted per requirements
            if (signatureFile) fd.append('Signature', signatureFile);

            const response = await authorAuthService.updateProfile(fd);

            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Profile updated successfully' });
                setIsEditing(false);
                setSignatureFile(null);
                fetchProfile();
                updateProfileCompletion(true);
            } else {
                throw new Error(response.message || 'Update failed');
            }
        } catch (error: any) {
            showNotification('error', { title: 'Error', message: error.message || 'Failed to update profile' });
        } finally {
            setSaving(false);
        }
    };

    const inputClass = "w-full rounded-lg border-gray-300 border px-4 py-2.5 focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";
    const sectionClass = "bg-white rounded-xl border border-gray-100 p-6 mb-6 shadow-sm";

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#088A61]"></div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto py-8 px-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
                    <p className="text-gray-500 mt-1">Manage your personal information and preferences</p>
                </div>
                {!isEditing && activeTab === 'personal' && (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-[#088A61] text-white rounded-lg hover:bg-[#067451] transition-colors font-medium shadow-sm"
                    >
                        <i className="bi bi-pencil-square"></i>
                        Edit Profile
                    </button>
                )}
            </div>

            {/* Tabs — only visible to reviewers */}
            {isReviewer && (
                <div className="border-b border-gray-200 mb-6">
                    <nav className="flex gap-1">
                        {([
                            { id: 'personal', label: 'Personal Info', icon: 'bi-person' },
                            { id: 'bank',     label: 'Bank Details',  icon: 'bi-bank2' },
                        ] as { id: ProfileTab; label: string; icon: string }[]).map(t => {
                            const active = activeTab === t.id;
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => setActiveTab(t.id)}
                                    className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors border-b-2 -mb-px ${
                                        active
                                            ? 'border-[#088A61] text-[#088A61]'
                                            : 'border-transparent text-gray-500 hover:text-gray-800'
                                    }`}
                                >
                                    <i className={`bi ${t.icon}`}></i>
                                    {t.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>
            )}

            {activeTab === 'personal' && (
            <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    <div className="lg:col-span-1">
                        <div className={sectionClass}>
                            <div className="flex flex-col items-center py-6">
                                <div className="w-32 h-32 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-4xl font-bold mb-4 border-4 border-white shadow-md">
                                    {profile?.person?.firstName?.[0]}{profile?.person?.lastName?.[0]}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 text-center">
                                    {profile?.person?.fullName}
                                </h3>
                                <p className="text-sm text-gray-500 text-center mb-4">
                                    {typeof profile?.role === 'string' ? profile?.role : profile?.role?.name}
                                </p>
                                <div className={`px-3 py-1 rounded-full text-xs font-medium ${profile?.person?.profileCompleted ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                    {profile?.person?.profileCompleted ? 'Profile Completed' : 'Profile Incomplete'}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-2">

                        <div className={sectionClass}>
                            <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                                <i className="bi bi-person text-[#088A61]"></i>
                                Personal Information
                            </h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className={labelClass}>Title <span className="text-red-500">*</span></label>
                                    <select
                                        name="titleId"
                                        value={formData.titleId || ''}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className={inputClass}
                                    >
                                        <option value="">Select Title</option>
                                        {titles.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className={labelClass}>First Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className={inputClass}
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>Last Name <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className={inputClass}
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>Other Name</label>
                                    <input
                                        type="text"
                                        name="otherName"
                                        value={formData.otherName}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className={inputClass}
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>Gender <span className="text-red-500">*</span></label>
                                    <select
                                        name="sexId"
                                        value={formData.sexId || ''}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className={inputClass}
                                    >
                                        <option value="">Select Gender</option>
                                        <option value={1}>Male</option>
                                        <option value={2}>Female</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className={sectionClass}>
                            <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                                <i className="bi bi-telephone text-[#088A61]"></i>
                                Contact Information
                            </h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="col-span-1 md:col-span-2">
                                    <label className={labelClass}>Email Address</label>
                                    <div className="relative">
                                        <i className="bi bi-envelope absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            disabled={true}
                                            className={`${inputClass} bg-gray-50 pl-3`}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className={labelClass}>Phone Number <span className="text-red-500">*</span></label>
                                    <div className="relative">
                                        <i className="bi bi-phone absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                                        <input
                                            type="tel"
                                            name="phoneNo"
                                            value={formData.phoneNo}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            className={`${inputClass} pl-3`}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className={labelClass}>State of Origin <span className="text-red-500">*</span></label>
                                    <select
                                        name="stateId"
                                        value={formData.stateId || ''}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className={inputClass}
                                    >
                                        <option value="">Select State</option>
                                        {states.map(s => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="col-span-1 md:col-span-2">
                                    <label className={labelClass}>Official Address <span className="text-red-500">*</span></label>
                                    <textarea
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        rows={3}
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={sectionClass}>
                            <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                                <i className="bi bi-mortarboard text-[#088A61]"></i>
                                Academic Information
                            </h4>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {!isOtherInstitution && (
                                    <>
                                        <div className="col-span-1 md:col-span-1">
                                            <label className={labelClass}>Institution Category <span className="text-red-500">*</span></label>
                                            {isEditing ? (
                                                <select
                                                    name="institutionCategoryId"
                                                    value={formData.institutionCategoryId}
                                                    onChange={handleInputChange}
                                                    className={inputClass}
                                                >
                                                    <option value="">Select Category</option>
                                                    {institutionCategories.map(cat => (
                                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={profile?.person?.institution?.institutionCategory || 'N/A'}
                                                    disabled={true}
                                                    className={`${inputClass} bg-gray-50`}
                                                />
                                            )}
                                        </div>

                                        <div className="col-span-1 md:col-span-1">
                                            <label className={labelClass}>Institution Type <span className="text-red-500">*</span></label>
                                            {isEditing ? (
                                                <select
                                                    name="institutionIsPublic"
                                                    value={formData.institutionIsPublic}
                                                    onChange={handleInputChange}
                                                    className={inputClass}
                                                >
                                                    <option value="">Select Type</option>
                                                    <option value="true">Public</option>
                                                    <option value="false">Private</option>
                                                </select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={profile?.person?.institution?.isPublic === true ? 'Public' : profile?.person?.institution?.isPublic === false ? 'Private' : 'N/A'}
                                                    disabled={true}
                                                    className={`${inputClass} bg-gray-50`}
                                                />
                                            )}
                                        </div>
                                    </>
                                )}

                                <div className="col-span-1 md:col-span-2">
                                    <label className={labelClass}>Affiliate Institution <span className="text-red-500">*</span></label>
                                    <div className="relative">
                                        <i className="bi bi-bank absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                                        {isOtherInstitution ? (
                                            <input
                                                type="text"
                                                value={profile?.person?.affiliateInstitution || 'N/A'}
                                                disabled={true}
                                                className={`${inputClass} bg-gray-50 pl-3`}
                                            />
                                        ) : isEditing ? (
                                            <select
                                                name="institutionId"
                                                value={formData.institutionId || ''}
                                                onChange={handleInputChange}
                                                className={`${inputClass} pl-3`}
                                            >
                                                <option value="">Select Institution</option>
                                                {institutions.map(inst => (
                                                    <option key={inst.id} value={inst.id}>{inst.name}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                value={profile?.person?.institution?.name || 'N/A'}
                                                disabled={true}
                                                className={`${inputClass} bg-gray-50 pl-3`}
                                            />
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className={labelClass}>Rank <span className="text-red-500">*</span></label>
                                    <select
                                        name="rankId"
                                        value={formData.rankId || ''}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className={inputClass}
                                    >
                                        <option value="">Select Rank</option>
                                        {ranks.map(r => (
                                            <option key={r.id} value={r.id}>{r.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className={labelClass}>Qualification <span className="text-red-500">*</span></label>
                                    <select
                                        name="qualificationId"
                                        value={formData.qualificationId || ''}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className={inputClass}
                                    >
                                        <option value="">Select Qualification</option>
                                        {qualifications.map(q => (
                                            <option key={q.id} value={q.id}>{q.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {isES && (
                            <div className={sectionClass}>
                                <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                                    <i className="bi bi-pen text-[#088A61]"></i>
                                    Signature <span className="text-red-500 text-sm">*</span>
                                </h4>

                                {signatureUrl && (
                                    <div className="mb-4">
                                        <p className={labelClass}>Current Signature</p>
                                        <img
                                            src={signatureUrl}
                                            alt="Signature"
                                            className="max-h-32 rounded-lg border border-gray-200 bg-gray-50 p-2 object-contain"
                                        />
                                    </div>
                                )}

                                {isEditing ? (
                                    <div>
                                        <label className={labelClass}>
                                            {signaturePath ? 'Replace Signature' : 'Upload Signature'}
                                            {!signaturePath && <span className="text-red-500"> *</span>}
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => setSignatureFile(e.target.files?.[0] || null)}
                                            className={inputClass}
                                        />
                                        {signatureFile && (
                                            <p className="mt-1 text-xs text-gray-500">Selected: {signatureFile.name}</p>
                                        )}
                                        <p className="mt-1 text-xs text-gray-500">Upload a clear image of your signature (PNG or JPG).</p>
                                    </div>
                                ) : (
                                    !signaturePath && (
                                        <p className="text-sm text-gray-500">No signature uploaded yet.</p>
                                    )
                                )}
                            </div>
                        )}

                        {isEditing && (
                            <div className="flex justify-end gap-4 mt-8">
                                <button
                                    type="button"
                                    onClick={() => { setIsEditing(false); setSignatureFile(null); }}
                                    disabled={saving}
                                    className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving || !isFormValid()}
                                    className={`px-6 py-2.5 rounded-lg font-medium transition-colors shadow-lg flex items-center gap-2 ${!isFormValid()
                                        ? 'bg-gray-400 cursor-not-allowed text-white'
                                        : 'bg-[#088A61] text-white hover:bg-[#067451] shadow-green-900/10'
                                        }`}
                                >
                                    {saving && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </form>
            )}

            {isReviewer && activeTab === 'bank' && (
                <BankDetailsTab />
            )}
        </div>
    );
};

// ── Bank Details tab (reviewers only) ───────────────────────────────────────

const BankDetailsTab: React.FC = () => {
    const { showNotification } = useNotification();
    const [loading, setLoading] = useState(true);
    const [bank, setBank] = useState<BankAccountDetail | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState<BankAccountDetail>({
        id: ZERO_GUID,
        accountName: '',
        accountNumber: '',
        bankName: '',
    });

    const load = async () => {
        setLoading(true);
        try {
            const res = await apiClient.get('/BankAccountDetails/GetMyBankAccountDetail');
            const data = res.data?.data;
            const hasAny = data && (data.accountName || data.accountNumber || data.bankName);
            if (res.data?.isSuccess && hasAny) {
                setBank({
                    id: data.id ?? ZERO_GUID,
                    accountName: data.accountName ?? '',
                    accountNumber: data.accountNumber ?? '',
                    bankName: data.bankName ?? '',
                });
            } else {
                setBank(null);
            }
        } catch {
            setBank(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setForm({ id: ZERO_GUID, accountName: '', accountNumber: '', bankName: '' });
        setModalOpen(true);
    };

    const openUpdate = () => {
        if (!bank) return;
        setForm({ ...bank });
        setModalOpen(true);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                id: form.id || ZERO_GUID,
                accountName: form.accountName.trim(),
                accountNumber: form.accountNumber.trim(),
                bankName: form.bankName.trim(),
            };
            const res = await apiClient.post('/BankAccountDetails/CreateUpdateBankAccountDetail', payload);
            if (res.data?.isSuccess) {
                showNotification('success', { title: 'Saved', message: bank ? 'Bank details updated successfully.' : 'Bank details added successfully.' });
                setModalOpen(false);
                await load();
                // Let the bank-details gate re-check and dismiss if it was blocking.
                window.dispatchEvent(new Event('bank-details-updated'));
            } else {
                showNotification('error', { title: 'Error', message: res.data?.message || 'Failed to save bank details.' });
            }
        } catch (err: any) {
            showNotification('error', { title: 'Error', message: err?.response?.data?.message || err?.message || 'Failed to save bank details.' });
        } finally {
            setSaving(false);
        }
    };

    const isFormValid =
        form.accountName.trim().length > 0 &&
        form.accountNumber.length === 10 &&
        form.bankName.trim().length > 0;
    const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
    const inputClass = 'w-full rounded-lg border-gray-300 border px-4 py-2.5 focus:ring-2 focus:ring-[#088A61] focus:border-transparent outline-none transition-all';

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[300px]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#088A61]"></div>
            </div>
        );
    }

    return (
        <>
            <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <i className="bi bi-bank2 text-[#088A61]"></i>
                        Bank Account Details
                    </h4>
                    {bank && (
                        <button
                            onClick={openUpdate}
                            className="flex items-center gap-2 px-4 py-2 bg-[#088A61] text-white rounded-lg hover:bg-[#067451] transition-colors font-medium text-sm shadow-sm"
                        >
                            <i className="bi bi-pencil-square"></i>
                            Update Bank Details
                        </button>
                    )}
                </div>

                {bank ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className={labelClass}>Account Name</label>
                            <input type="text" value={bank.accountName} disabled className={`${inputClass} bg-gray-50`} />
                        </div>
                        <div>
                            <label className={labelClass}>Account Number</label>
                            <input type="text" value={bank.accountNumber} disabled className={`${inputClass} bg-gray-50`} />
                        </div>
                        <div>
                            <label className={labelClass}>Bank Name</label>
                            <input type="text" value={bank.bankName} disabled className={`${inputClass} bg-gray-50`} />
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                        <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                            <i className="bi bi-bank2 text-2xl text-[#088A61]"></i>
                        </div>
                        <h5 className="text-base font-semibold text-gray-800 mb-1">No bank details yet</h5>
                        <p className="text-sm text-gray-500 mb-5">Add the account where your reviewer payments should be made.</p>
                        <button
                            onClick={openCreate}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#088A61] text-white rounded-lg hover:bg-[#067451] transition-colors font-medium text-sm shadow-sm"
                        >
                            <i className="bi bi-plus-lg"></i>
                            Create Bank Details
                        </button>
                    </div>
                )}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={!saving ? () => setModalOpen(false) : undefined}
                    />
                    <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
                        <div className="px-6 py-5 bg-gradient-to-r from-[#088A61] to-[#065f44]">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <h3 className="text-base font-black text-white">
                                        {bank ? 'Update Bank Details' : 'Add Bank Details'}
                                    </h3>
                                    <p className="text-xs text-white/70 mt-0.5">
                                        Enter the account where payments will be sent.
                                    </p>
                                </div>
                                {!saving && (
                                    <button
                                        onClick={() => setModalOpen(false)}
                                        className="text-white/60 hover:text-white transition-colors flex-shrink-0"
                                        aria-label="Close"
                                    >
                                        <i className="bi bi-x-lg text-lg"></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
                            <div>
                                <label className={labelClass}>Account Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={form.accountName}
                                    onChange={e => setForm(prev => ({ ...prev, accountName: e.target.value }))}
                                    className={inputClass}
                                    required
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Account Number <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={10}
                                    value={form.accountNumber}
                                    onChange={e => setForm(prev => ({ ...prev, accountNumber: e.target.value.replace(/[^0-9]/g, '').slice(0, 10) }))}
                                    className={inputClass}
                                    required
                                />
                                <p className="mt-1 text-xs text-gray-500">{form.accountNumber.length}/10 digits</p>
                            </div>
                            <div>
                                <label className={labelClass}>Bank Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={form.bankName}
                                    onChange={e => setForm(prev => ({ ...prev, bankName: e.target.value }))}
                                    className={inputClass}
                                    required
                                />
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    disabled={saving}
                                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-60"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving || !isFormValid}
                                    className={`flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-colors ${
                                        saving || !isFormValid
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-[#088A61] text-white hover:bg-[#067451]'
                                    }`}
                                >
                                    {saving
                                        ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving…</>
                                        : <>{bank ? 'Update' : 'Create'}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default Profile;
